<template>
  <div
    ref="container"
    class="configurator-3d-view"
    :style="{ backgroundPositionY: activeSize && activeSize.shadowPositionY }"
  >
    <canvas
      ref="canvas"
      :class="{ 'configurator-3d-view__canvas--grabbing': isGrabbing }"
      :style="canvasStyle"
      class="configurator-3d-view__canvas"
      @mousedown="grab"
      @mouseleave="releaseGrab"
      @mouseup="releaseGrab"
    ></canvas>
  </div>
</template>

<script>
import {
  AmbientLight,
  BackSide,
  DirectionalLight,
  Group,
  LinearFilter,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  RectAreaLight,
  Scene,
  Spherical,
  sRGBEncoding,
  TextureLoader,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer/lib/ResizeObserver';
import { fallbackToPx } from '../../utils/CssUtils';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import PromiseHelper from '../../utils/PromiseHelper';
import CanvasExport from '../../mixins/CanvasExport';
import { canvasToBlob } from '../../utils/ImageUtils';
import Model3dRotation from '../../mixins/Model3dRotation';
import {
  ConfiguratorGetters,
  mapGetters as mapConfiguratorGetters,
  mapState as mapConfiguratorState,
} from '../../store/modules/configurator';
// TODO Update import to take advantage of tree shaking
import TWEEN from '@tweenjs/tween.js';

const ResizeObserver = globalThis.ResizeObserver || ResizeObserverPolyfill;

const PromiseKey = Object.freeze({
  SCENE: 0,
  GLTF: 1,
  TEXTURE: 2,
  CAMERA_POSITION: 3,
});

const PHI_ANGLE = Math.PI / 2.3;
const ORBIT_CONTROL_SPEED = -6;

export default {
  name: 'Configurator3dView',
  mixins: [CanvasExport, Model3dRotation],
  props: {
    gltf: {
      type: String,
      default: undefined,
    },
    texture: {
      type: String,
      default: undefined,
    },
    cameraPosition: {
      type: Array,
      default: () => [0, 5, 0],
      validator(v) {
        return (
          v instanceof Array &&
          v.length === 3 &&
          v.every(axis => typeof axis === 'number')
        );
      },
    },
    autoRotate: {
      type: Boolean,
    },
    thetaAngle: {
      type: Number,
      default: undefined,
    },
    headless: {
      type: Boolean,
    },
  },
  data() {
    return {
      resizeObserver: null,
      canvasDimensions: {
        width: null,
        height: null,
      },
      isGrabbing: false,
      // Threejs variables
      scene: null,
      camera: null,
      renderer: null,
      orbitControls: null,
      gltfModel: null,
      insideMesh: null,
      rafId: null,
      gltfScene: null,
      outsideMaterial: null,
      insideMaterial: null,
      cameraLightGroup: null,
      // Promise
      promiseHelper: new PromiseHelper(),
      // Render throttling flag
      pendingRenderRequest: false,
      // Destruction guard for async operations
      isDestroyed: false,
    };
  },
  computed: {
    canvasStyle() {
      const styles = {
        width: fallbackToPx(this.canvasDimensions.width),
        height: fallbackToPx(this.canvasDimensions.height),
      };

      return styles;
    },
    aspect() {
      return (
        this.canvasDimensions.width &&
        this.canvasDimensions.height &&
        this.canvasDimensions.width / this.canvasDimensions.height
      );
    },
    ...mapConfiguratorGetters({
      activeSize: ConfiguratorGetters.GET_ACTIVE_SIZE,
      activeColor: ConfiguratorGetters.GET_ACTIVE_COLOR,
    }),
    ...mapConfiguratorState(['activeColorId']),
    canPlay() {
      return (
        this.autoRotate &&
        this.$store.state.configurator.focusedCustomizationId === null
      );
    },
    activeMaterialOpacity() {
      return (this.activeColor && this.activeColor.materialOpacity) || 1;
    },
  },
  watch: {
    /**
     *
     * @param {{width: number|null, height: number|null}} newCanvasDimensions
     * @param {{width: number|null, height: number|null}} oldCanvasDimensions
     */
    async canvasDimensions(newCanvasDimensions, oldCanvasDimensions) {
      if (
        oldCanvasDimensions.width !== null ||
        oldCanvasDimensions.height !== null
      ) {
        if (this.camera) {
          this.camera.aspect = this.aspect;
          this.camera.updateProjectionMatrix();
        }

        const renderer = await this.getRenderer();

        renderer.setPixelRatio(window.devicePixelRatio || 1);

        if (renderer) {
          renderer.setSize(
            newCanvasDimensions.width,
            newCanvasDimensions.height
          );
        }

        this.render(true);
      }
    },
    async cameraPosition([x, y, z]) {
      if (this.isDestroyed) {
        return;
      }

      this.promiseHelper.request(PromiseKey.CAMERA_POSITION);

      const camera = this.getCamera();
      const orbitControls = await this.getOrbitControls();

      if (this.isDestroyed) {
        return;
      }

      orbitControls.enabled = false;

      camera.position.set(x, y, z);
      camera.lookAt(orbitControls.target);

      await this.render(true);

      if (this.isDestroyed) {
        return;
      }

      orbitControls.enabled = true;

      this.promiseHelper.resolve(PromiseKey.CAMERA_POSITION);
    },
    async activeMaterialOpacity(opacity) {
      await this.setTexture(this.texture, opacity);
    },
  },
  created() {
    this.promiseHelper.request(PromiseKey.SCENE);
    this.promiseHelper.request(PromiseKey.CAMERA_POSITION);

    // Use vm.$watch() in created hook instead of standard watchers because the first call
    // when `immediate` is set to `true` occurs before the `created` hook. At this time,
    // data observation is not available yet.
    this.$watch(
      'gltf',
      v => {
        this.promiseHelper
          .request(PromiseKey.GLTF)
          .get(PromiseKey.GLTF)
          .then(url => {
            this.$emit('gltf-ready', url);
          });

        this.setGltf(v);
      },
      { immediate: true }
    );

    this.$watch(
      'texture',
      async v => {
        this.promiseHelper
          .request(PromiseKey.TEXTURE)
          .get(PromiseKey.TEXTURE)
          .then(url => {
            this.$emit('texture-ready', url);
          });

        await this.setTexture(v, this.activeMaterialOpacity);
      },
      { immediate: true }
    );

    this.$watch(
      'thetaAngle',
      async v => {
        if (v === undefined) {
          return;
        }

        const [, radius] = this.cameraPosition;

        await this.tweenCamera(new Spherical(radius, PHI_ANGLE, v));
      },
      { immediate: true }
    );
  },
  async mounted() {
    await this.initResizeObserver();

    this.promiseHelper.resolve(PromiseKey.SCENE);
    this.$emit('scene-ready');

    this.$watch(
      'canPlay',
      async v => {
        /** @type {OrbitControls} */
        const orbitControls = await this.getOrbitControls();

        if (v) {
          orbitControls.removeEventListener(
            'change',
            this.orbitControlsChangeCb
          );

          orbitControls.autoRotate = v;

          this.render();
        } else {
          orbitControls.autoRotate = v;

          this.pauseRender();

          // Re-render only when camera moves
          // See https://stackoverflow.com/a/23771782/1224564
          orbitControls.addEventListener('change', this.orbitControlsChangeCb);
        }
      },
      { immediate: true }
    );
  },
  beforeDestroy() {
    this.isDestroyed = true;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.disposeWebGLResources();
  },
  methods: {
    /**
     *
     * @param {Spherical} targetSpherical
     * @param {number} duration
     * @return {Promise<unknown>}
     */
    async tweenCamera(targetSpherical, duration = 150) {
      /** @type {OrbitControls} */
      const controls = await this.getOrbitControls();

      return new Promise(resolve => {
        const camera = this.getCamera();

        const sphericalPosition = new Spherical().setFromVector3(
          camera.position
        );

        const tween = new TWEEN.Tween(sphericalPosition)
          .to(targetSpherical, duration)
          .easing(TWEEN.Easing.Linear.None)
          .onUpdate(() => {
            camera.position.setFromSpherical(sphericalPosition);
            camera.lookAt(controls.target);
          })
          .onComplete(() => {
            camera.position.setFromSpherical(sphericalPosition);
            camera.lookAt(controls.target);

            // Pause render loop on animation end
            if (!this.canPlay) {
              this.pauseRender();
            }

            controls.enabled = true;

            resolve();
          });

        controls.enabled = false;

        // Start render loop
        this.render(false);

        tween.start();
      });
    },
    initResizeObserver() {
      return new Promise(resolve => {
        this.resizeObserver = new ResizeObserver(entries => {
          entries.forEach(({ contentRect: { width, height } }) => {
            this.canvasDimensions = { width, height };
          });

          resolve();
        });
        this.resizeObserver.observe(this.$refs.container);
      });
    },
    getCameraLightGroup() {
      if (!this.cameraLightGroup) {
        const lightIntesity = 0.6;
        const lightColor = '#fff';
        const lightPositionHeight = 100;
        const stripLightIntensity = 0.05;
        const stripLightWidth = 40;
        const stripLightHeight = 600;

        const rightFrontLight = new DirectionalLight(lightColor, lightIntesity);
        rightFrontLight.position.set(230, lightPositionHeight, 100);

        const leftStrip = new RectAreaLight(
          lightColor,
          stripLightIntensity,
          stripLightWidth,
          stripLightHeight
        );
        leftStrip.position.set(-120, -50, 10);
        leftStrip.lookAt(0, -70, 0);

        const rightStrip = new RectAreaLight(
          lightColor,
          stripLightIntensity,
          stripLightWidth,
          stripLightHeight
        );
        rightStrip.position.set(120, -50, 10);
        rightStrip.lookAt(0, -70, 0);

        this.cameraLightGroup = new Group();

        this.cameraLightGroup.add(leftStrip);
        this.cameraLightGroup.add(rightStrip);
        this.cameraLightGroup.add(rightFrontLight);
      }

      return this.cameraLightGroup;
    },
    /**
     * @return {Scene}
     */
    getScene() {
      if (!this.scene) {
        this.scene = new Scene();

        // Add light to scene
        const light = new AmbientLight(0xffffff, 0.8);
        this.scene.add(light);

        this.scene.add(this.getCameraLightGroup());
      }

      return this.scene;
    },
    /**
     * @return {PerspectiveCamera}
     */
    getCamera() {
      if (!this.camera) {
        this.camera = new PerspectiveCamera(23, this.aspect, 0.01, 10);

        const [x, y, z] = this.cameraPosition;

        this.camera.position = new Vector3(x, y, z);

        this.promiseHelper.resolve(PromiseKey.CAMERA_POSITION);
      }

      return this.camera;
    },
    /**
     *
     * @return {Promise<WebGLRenderer>}
     */
    async getRenderer() {
      if (!this.renderer) {
        this.renderer = this.promiseHelper.get(PromiseKey.SCENE).then(() => {
          if (this.isDestroyed) {
            throw new Error(
              'Component destroyed before renderer could be created'
            );
          }

          const canvas = this.$refs.canvas;

          if (!canvas) {
            throw new Error('Canvas not found');
          }

          const renderer = new WebGLRenderer({
            antialias: true,
            alpha: true,
            canvas,
            preserveDrawingBuffer: true,
          });

          // Headless renderers only produce export images (cart previews,
          // cross-sell). They never need the device pixel ratio (3 on iPhone),
          // which would allocate ~9x larger readback canvases and pile up
          // memory on Safari iOS until the tab is killed. The on-screen
          // interactive view keeps the device pixel ratio to stay sharp.
          const pixelRatio = this.headless ? 1 : window.devicePixelRatio || 1;
          renderer.setPixelRatio(pixelRatio);

          renderer.gammaOutput = true;
          renderer.gammaFactor = 2.2;

          renderer.setSize(
            this.canvasDimensions.width,
            this.canvasDimensions.height
          );

          // [DEBUG 518999] Diagnose incomplete screenshots on Safari iOS.
          // Remove once the root cause is identified.
          const debugGl = renderer.getContext();
          console.info('[518999] renderer created', {
            maxTextureSize: debugGl.getParameter(debugGl.MAX_TEXTURE_SIZE),
            pixelRatio,
            size: `${this.canvasDimensions.width}x${this.canvasDimensions.height}`,
          });
          canvas.addEventListener('webglcontextlost', event => {
            console.error('[518999] webglcontextlost', event);
          });
          canvas.addEventListener('webglcontextrestored', () => {
            console.warn('[518999] webglcontextrestored');
          });

          return renderer;
        });
      }

      return this.renderer;
    },
    orbitControlsChangeCb() {
      // Throttle render calls to sync with display refresh rate
      // This prevents multiple renders per frame which causes flickering
      if (!this.pendingRenderRequest) {
        this.pendingRenderRequest = true;
        globalThis.requestAnimationFrame(() => {
          this.pendingRenderRequest = false;
          this.render(true);
        });
      }
    },
    /**
     * @return {Promise<OrbitControls>}
     */
    async getOrbitControls() {
      if (!this.orbitControls) {
        this.orbitControls = this.getRenderer().then(renderer => {
          const orbitControls = new OrbitControls(
            this.getCamera(),
            renderer.domElement
          );

          // Disable zoom
          orbitControls.enableZoom = false;

          // Disable rotation
          orbitControls.enableRotate = true;

          // Disable pan
          orbitControls.enablePan = false;

          orbitControls.enableDamping = false;
          orbitControls.update();

          orbitControls.minPolarAngle = PHI_ANGLE;
          orbitControls.maxPolarAngle = PHI_ANGLE;

          orbitControls.autoRotateSpeed = ORBIT_CONTROL_SPEED;

          return orbitControls;
        });
      }

      return this.orbitControls;
    },
    /**
     *
     * @param url
     * @return {Promise<GLTF>}
     */
    loadGltf(url) {
      return new Promise((resolve, reject) => {
        const modelLoader = new GLTFLoader();

        modelLoader.load(
          url,
          gltf => resolve(gltf),
          undefined,
          error => reject(error)
        );
      });
    },
    /**
     *
     * @param gltfUrl
     * @return {Promise<void>}
     */
    async setGltf(gltfUrl) {
      await this.promiseHelper.get(PromiseKey.SCENE);

      if (gltfUrl) {
        const gltf = await this.loadGltf(gltfUrl);

        const scene = this.getScene();

        if (this.gltfScene) {
          scene.remove(this.gltfScene);
        }

        this.gltfScene = gltf.scene;
        this.gltfModel = gltf.scene.children[0];
        this.gltfModel.position.y = 0;

        const insideMesh = new Mesh(this.gltfModel.geometry);
        insideMesh.scale.copy(this.gltfModel.scale);
        gltf.scene.add(insideMesh);

        this.insideMesh = insideMesh;

        scene.add(gltf.scene);
      }

      await this.render(true);

      // Do not emit anything if the loaded GLTF URL is not the current one
      if (gltfUrl === this.gltf) {
        this.promiseHelper.resolve(PromiseKey.GLTF, gltfUrl);
      }
    },
    /**
     *
     * @param {boolean} singleFrame
     */
    async render(singleFrame = false) {
      // Use local variables for performance (avoid function call in each loop iteration)
      const renderer = await this.getRenderer();
      const scene = this.getScene();
      const camera = this.getCamera();
      const orbitControls = await this.getOrbitControls();
      const cameraLightGroup = this.getCameraLightGroup();

      const renderFrame = () => {
        // Update lights BEFORE rendering to keep them in sync with camera
        cameraLightGroup.quaternion.copy(camera.quaternion);
        orbitControls.update();

        TWEEN.update();

        renderer.render(scene, camera);

        // [DEBUG 518999] Surface GPU failures (out-of-memory, context loss)
        // that otherwise fail silently and yield a blank screenshot.
        const debugGl = renderer.getContext();
        if (debugGl.isContextLost()) {
          console.error('[518999] context lost during render');
        } else {
          const debugErr = debugGl.getError();
          if (debugErr !== 0) {
            console.error('[518999] gl error after render', debugErr);
          }
        }

        if (!singleFrame && this.rafId) {
          this.rafId = globalThis.requestAnimationFrame(renderFrame);
        }
      };

      return new Promise(resolve => {
        // render the first frame
        this.rafId = globalThis.requestAnimationFrame(() => {
          renderFrame();

          resolve();
        });
      });
    },
    pauseRender() {
      if (this.rafId) {
        globalThis.cancelAnimationFrame(this.rafId);

        this.rafId = null;
      }
    },
    grab() {
      if (!this.isGrabbing) {
        this.isGrabbing = true;
      }
    },
    releaseGrab() {
      if (this.isGrabbing) {
        this.isGrabbing = false;
      }
    },
    /**
     *
     * @param url
     * @return {Promise<Texture>}
     */
    loadTexture(url) {
      return new Promise((resolve, reject) => {
        const modelLoader = new TextureLoader();

        modelLoader.load(
          url,
          texture => resolve(texture),
          undefined,
          error => reject(error)
        );
      });
    },
    async setTexture(textureImage, opacity) {
      await this.promiseHelper.get(PromiseKey.SCENE);

      if (textureImage) {
        const texture = await this.loadTexture(textureImage);

        // [DEBUG 518999]
        console.info('[518999] texture loaded', {
          srcLength:
            typeof textureImage === 'string' ? textureImage.length : null,
          width: texture.image && texture.image.width,
          height: texture.image && texture.image.height,
          opacity,
        });

        // Dispose the previous GPU texture before replacing it
        if (this.insideMaterial && this.insideMaterial.map) {
          this.disposeTexture(this.insideMaterial.map);
        }
        if (this.outsideMaterial && this.outsideMaterial.map) {
          this.disposeTexture(this.outsideMaterial.map);
        }

        await this.promiseHelper.get(PromiseKey.GLTF);

        texture.encoding = sRGBEncoding;
        texture.center = new Vector2(0.5, 0.5);
        texture.anisotropy = 16;
        texture.repeat.set(1, 1);

        if (!this.outsideMaterial) {
          this.outsideMaterial = new MeshStandardMaterial();
          this.outsideMaterial.transparent = true;
          this.outsideMaterial.side = BackSide;
        }

        this.outsideMaterial.opacity = opacity;

        // apply texture on BackSide only when opacity is turned off
        if (opacity !== 1) {
          this.outsideMaterial.map = texture;
          // Turn off MIP mapping
          // See https://stackoverflow.com/a/55176042/1224564
          this.outsideMaterial.map.minFilter = LinearFilter;
        } else {
          // if opacity is off, we hide it.
          this.outsideMaterial.opacity = 0;
        }

        if (!this.insideMaterial) {
          this.insideMaterial = new MeshStandardMaterial();
          this.insideMaterial.transparent = true;
        }

        this.insideMaterial.opacity = opacity;
        this.insideMaterial.map = texture;
        this.insideMaterial.map.minFilter = LinearFilter;
        this.insideMesh.material = this.insideMaterial;

        this.gltfModel.material = this.outsideMaterial;
      }

      await this.render(true);

      this.promiseHelper.resolve(PromiseKey.TEXTURE, textureImage);
    },
    /**
     * Dispose a Three.js texture and its associated GPU resources.
     * @param {import('three').Texture|null} texture
     */
    disposeTexture(texture) {
      if (texture) {
        texture.dispose();
      }
    },
    /**
     * Dispose all WebGL resources to prevent memory leaks on low-memory devices (e.g. Safari iOS).
     */
    async disposeWebGLResources() {
      this.pauseRender();

      this.disposeTexture(this.outsideMaterial && this.outsideMaterial.map);
      this.disposeTexture(this.insideMaterial && this.insideMaterial.map);

      if (this.outsideMaterial) {
        this.outsideMaterial.dispose();
        this.outsideMaterial = null;
      }

      if (this.insideMaterial) {
        this.insideMaterial.dispose();
        this.insideMaterial = null;
      }

      // Free the GLTF geometries: dispose() below does not touch scene geometry,
      // so without this each cross-sell product leaks its mesh data.
      if (this.gltfScene) {
        this.gltfScene.traverse(object => {
          if (object.geometry) {
            object.geometry.dispose();
          }
        });
        this.gltfScene = null;
      }

      if (this.renderer) {
        const renderer = await this.renderer;
        renderer.dispose();
        // dispose() frees three.js-managed GPU objects, but on iOS Safari the
        // underlying WebGL context lingers until garbage collection. Rendering
        // cross-sell products one after another then piles up contexts until
        // the tab is killed. forceContextLoss() releases the context now.
        renderer.forceContextLoss();
        this.renderer = null;
      }
    },
    /**
     * Override CanvasExport.toBlob() to use the native canvas.toBlob() API,
     * avoiding the expensive base64 round-trip which causes memory pressure on Safari iOS.
     * @return {Promise<Blob>}
     */
    async toBlob() {
      if (this.isDestroyed) {
        return null;
      }

      await this.renderReady();

      const canvas = this.$refs.canvas;

      if (!canvas) {
        throw new Error('Canvas not found');
      }

      const blob = await canvasToBlob(canvas);

      // [DEBUG 518999] Measure opaque coverage on a tiny probe: a full glass
      // render fills a large share of the frame, a "design only / glass
      // missing" render fills very little. blobBytes alone cannot tell them
      // apart. Remove once the root cause is identified.
      let opaqueRatio = null;
      try {
        const probe = document.createElement('canvas');
        probe.width = 60;
        probe.height = 100;
        const pctx = probe.getContext('2d');
        pctx.drawImage(canvas, 0, 0, probe.width, probe.height);
        const { data } = pctx.getImageData(0, 0, probe.width, probe.height);
        let opaque = 0;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] > 10) {
            opaque++;
          }
        }
        opaqueRatio = +(opaque / (probe.width * probe.height)).toFixed(2);
      } catch (e) {
        opaqueRatio = `err:${e.name}`;
      }

      console.info('[518999] toBlob', {
        size: `${canvas.width}x${canvas.height}`,
        blobBytes: blob && blob.size,
        opaqueRatio,
      });

      return blob;
    },
    /**
     *
     * @return {Promise<*[]>}
     */
    async renderReady() {
      const promiseHelper = this.promiseHelper;

      return Promise.all([
        promiseHelper.get(PromiseKey.SCENE),
        promiseHelper.get(PromiseKey.GLTF),
        promiseHelper.get(PromiseKey.TEXTURE),
        promiseHelper.get(PromiseKey.CAMERA_POSITION),
      ]);
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-3d-view {
  position: relative;
  background: url('../../../../images/configurator/shadow.svg') center
    bottom/contain no-repeat;

  &__canvas {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    cursor: grab;

    &--grabbing {
      cursor: grabbing;
    }

    &:focus {
      outline: none;
    }
  }
}
</style>
